import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {
    GET_MICROSOFT_LOGIN_URL_MUTATION,
    MicrosoftIcon,
    type GetOneTimeUseMicrosoftLoginUrlMutationType,
} from "@khan/auth-microsoft";
import {createReauthUrl, type LoginType} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import {doNotTranslate} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {redirect, type Location} from "@khan/urls";

import RequireReauth from "../require-reauth.tsx";

import ResponsiveLinkedAccountRow from "./responsive-linked-account-row.tsx";

interface Props {
    appleId: string | null | undefined;
    microsoftId: string | null | undefined;
    microsoftPrimaryEmail: string | null | undefined;
    facebookId: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    identifier: string | null | undefined;
    isClasslinkUser: boolean;
    location: Location;
    renderThirdPartyDisconnectButton: (provider: LoginType) => React.ReactNode;
    setWarningMessage: (message: string | null) => void;
}

export const LinkMicrosoftAccount: React.FC<Props> = ({
    microsoftId,
    microsoftPrimaryEmail,
    identifier,
    isClasslinkUser,
    setWarningMessage,
    appleId,
    googlePrimaryEmail,
    facebookId,
    renderThirdPartyDisconnectButton,
    location,
}) => {
    const [getMicrosoftLoginUrl] = useMutation(
        GET_MICROSOFT_LOGIN_URL_MUTATION,
    );
    // Get redirect URL that will display the success message.
    const reauthUrl = React.useMemo(
        () =>
            createReauthUrl({
                loginType: "microsoft",
                pathname: location.pathname,
            }),
        [location.pathname],
    );

    const handleConnectMicrosoftLogin = React.useCallback(async () => {
        const variables: GetOneTimeUseMicrosoftLoginUrlMutationType["variables"] =
            {
                action: "LINK",
                continueUrl: reauthUrl,
            };
        const result = await getMicrosoftLoginUrl(variables);
        if (
            !result ||
            result.status !== "success" ||
            result.data.getOneTimeUseMicrosoftLoginUrl?.error
        ) {
            let cause = "";
            let error;

            if (result) {
                if (result.status === "error") {
                    error = result.error;
                } else if (
                    result.status === "success" &&
                    result.data.getOneTimeUseMicrosoftLoginUrl?.error
                ) {
                    cause =
                        result.data.getOneTimeUseMicrosoftLoginUrl.error.code;
                }
            }

            Log.error("Sign in with Microsoft failed", Errors.Internal, {
                cause: error || cause,
            });
            // get redirect URL that will display the error message.
            const reauthUrlError = createReauthUrl({
                loginType: "microsoft",
                pathname: location.pathname,
                error: error ? error.cause : cause,
            });

            redirect(reauthUrlError);
            return;
        }
        if (result.data.getOneTimeUseMicrosoftLoginUrl?.redirectUrl) {
            redirect(result.data.getOneTimeUseMicrosoftLoginUrl?.redirectUrl);
        }
    }, [getMicrosoftLoginUrl, reauthUrl, location.pathname]);

    const connectMicrosoftButton = (
        <RequireReauth
            identifier={identifier}
            showClasslinkAuthProvider={isClasslinkUser}
            onSuccess={handleConnectMicrosoftLogin}
            onFailure={() => {}}
            hasMicrosoftButton={false}
            // We check the other providers if the user has an account
            // registered with them
            hasGoogleButton={!!googlePrimaryEmail}
            hasFacebookButton={!!facebookId}
            hasAppleButton={!!appleId}
        >
            {(invokeReauth) => (
                <Button
                    onClick={() => {
                        setWarningMessage(null);
                        invokeReauth();
                    }}
                    size="small"
                    kind="tertiary"
                >
                    {t`Connect with Microsoft`}
                </Button>
            )}
        </RequireReauth>
    );

    return (
        <ResponsiveLinkedAccountRow
            icon={
                <MicrosoftIcon
                    ariaHidden={true}
                    disabled={false}
                    size="large"
                />
            }
            label={doNotTranslate`Microsoft`}
            content={microsoftId ? microsoftPrimaryEmail : undefined}
            action={
                microsoftId
                    ? renderThirdPartyDisconnectButton("microsoft")
                    : connectMicrosoftButton
            }
        />
    );
};
